import React, { useEffect, useLayoutEffect, useState } from 'react';
import { defineMessages } from 'react-intl';
import { StandaloneSearchBox, useJsApiLoader } from '@react-google-maps/api';

import { IDatabase, format } from '../../services/database';
import { googleMapsProps, GOOGLE_MAPS_API_KEY } from '../../constants';


interface Props {
    intl: any
    renderPoweredBy: () => any
    handleKeyDown: any
    setTerm: (t: string) => void
    term: string
    database: IDatabase | null
    search: () => void
    setFilter: (k: string, t: any) => void
    filter: object
    location: google.maps.places.PlaceResult | undefined
    setLocation: (location: google.maps.places.PlaceResult | undefined) => void
    distance: number | null,
    setDistance: (d: number | null) => void
    mode: 'address' | 'keyword'
    setMode: (mode: 'address' | 'keyword') => void
}

export default (props: Props) => {
    const [searchBox, setSearchBox] = useState<google.maps.places.SearchBox>()

    const { mode, setMode } = props

    useLayoutEffect(() => {
        document?.getElementById?.("searchBar")?.parentElement?.classList?.add?.('col')
    })

    useEffect(() => {
        if (mode === 'keyword') props.setLocation(undefined)
    }, [mode])

    const { isLoaded } = useJsApiLoader(googleMapsProps as any)

    const messages = defineMessages({
        searchPrompt: {
            id: 'search.form.placeholder',
            defaultMessage: 'Type in keywords or use filters to narrow down search...',
        },
        bryanKeywordSearchPrompt: {
            id: 'search.form.placeholder',
            defaultMessage: 'Search by Keyword',
        },
        bryanAddressSearchPrompt: {
            id: 'search.form.placeholder',
            defaultMessage: 'Search by Address',
        },
    });

    const focusAreas = [
        {
            color: 'rgb(8,60,202)',
            icon: "fas fa-book-open",
            label: 'Academic Enrichment & Literacy',
        },
        {
            color: 'rgb(227,37,103)',
            icon: "fas fa-palette",
            label: 'Arts',
        },
        {
            color: 'rgb(24,178,237)',
            icon: "fas fa-graduation-cap",
            label: 'College & Career Readiness',
        },
        {
            color: 'rgb(234,187,61)',
            icon: "fas fa-users",
            label: 'Clubs',
        },
        {
            color: 'rgb(66,56,91)',
            icon: "fas fa-calculator",
            label: 'Math',
        },
        {
            color: 'rgb(234,187,61)',
            icon: "fas fa-hand-holding-heart",
            label: 'Social Emotional Learning (SEL)',
        },
        {
            color: 'rgb(18,46,86)',
            icon: "fas fa-basketball-ball",
            label: 'Sports & Health & Wellness',
        },
        {
            color: 'rgb(155,49,58)',
            icon: "fas fa-flask",
            label: 'STEM/STEAM',
        },
    ]

    const focusAreaKey = 'Program_Information_program_focus_area_'

    const toggleFilterValue = (key: string, value: string) => {
        let filterValue = props.filter[key]
        if (!Array.isArray(filterValue)) filterValue = [filterValue].filter(Boolean)
        console.log(key, filterValue, filterValue?.includes?.(value), filterValue.filter(v => v !== value))
        if (filterValue?.includes?.(value))
            props.setFilter(key, filterValue.filter(v => v !== value))
        else props.setFilter(key, [...filterValue, value])
    }

    const handleAutoCompletePlaceChange = async () => {
        if (searchBox?.getPlaces?.()?.length) {
            const place = searchBox?.getPlaces?.()?.[0]
            props.setLocation(place)
            console.log(place)
        }
    };

    const renderSearchFocusAreas = () => (
        <>
            <div className="row my-3">
                <h5 className="col">Program Focus Area</h5>
            </div>
            <div className="row">
                {focusAreas.map(f => {
                    const selected = props.filter[focusAreaKey]?.includes(f.label)
                    return (
                        <div key={f.label} className='col-lg-4 col-md-6 col-xs-12'>
                            <button className="primary-focus-btn mb-2 w-100" style={{ borderRadius: 5, background: selected ? f.color : 'white' }} onClick={() => toggleFilterValue(focusAreaKey, f.label)}>
                                <div className="d-flex align-items-center p-2 font-weight-bold" style={{ color: selected ? 'white' : 'black' }}>
                                    <div className="mr-2">
                                        <em className={f.icon} style={{ background: f.color }} />
                                    </div>
                                    <div className="text-center" style={{ width: '100%' }}>
                                        {f.label}
                                    </div>
                                </div>
                            </button>
                        </div>
                    )
                })}
            </div>
        </>
    )

    const renderSearchFilterOptions = (key: string, option: string) => (
        <div className="form-check" key={`option-${key}-${option}`}>
            <input className="form-check-input" type="checkbox" checked={!!props.filter[key]?.includes(option)} value={option} id={option} onChange={() => toggleFilterValue(key, option)} />
            <label className="form-check-label" htmlFor={option}>{option}</label>
        </div>
    )

    const renderSearchFilters = () => {
        const { database } = props
        const filters = database && database.filters ? database.filters : [];
        return (
            <>
                {!database ||
                    filters.map((x) => {
                        const field = database.fields.find(
                            (f) => format(f.group, f.name) === x
                        );
                        if (!field || x === focusAreaKey) return null;
                        return (
                            <div
                                key={field.name}
                                className="col-md-3 col-sm-6 form-group"
                            >
                                <label className="font-weight-bold" htmlFor={field.name}>{field.label}</label>
                                <div className='d-flex d-flex-row'>
                                    <div className="mr-1">{field.options.filter(o => !o.includes('This provider')).slice(0, 7).map(o => renderSearchFilterOptions(x, o))}</div>
                                    <div>{field.options.filter(o => !o.includes('This provider')).slice(7).map(o => renderSearchFilterOptions(x, o))}</div>
                                </div>
                            </div>
                        );
                    })}
            </>
        );
    }

    const renderSearchField = () => {
        if (mode === 'keyword') return (
            <div className="form-group col">
                <label>Search</label>
                <input
                    type="text"
                    className="form-control"
                    placeholder={props.intl.formatMessage(
                        messages.bryanKeywordSearchPrompt
                    )}
                    onKeyDown={props.handleKeyDown}
                    onChange={(e) => props.setTerm(e.currentTarget.value)}
                    value={props.term}
                />
            </div>
        )
        return isLoaded ? (
            <StandaloneSearchBox
                onLoad={ref => setSearchBox(ref)}
                onPlacesChanged={handleAutoCompletePlaceChange}
            >
                <div className="form-group" id="searchBar">
                    <label>Address</label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder={props.intl.formatMessage(
                            messages.bryanAddressSearchPrompt
                        )}
                    />
                </div>
            </StandaloneSearchBox>
        ) : null
    }

    return (
        <div className="d-flex flex-fill wrap">
            <div className="search-container mx-2">
                <div className="row">
                    <div className="spage-title">
                        <h2>Baton Rouge Area Youth Network (BRAYN) Youth Program Locator</h2>
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-2 col-sm-12">
                        <label>Search By</label>
                        <select className="form-control" value={mode} onChange={(e) => setMode(e.currentTarget.value as any)}>
                            <option value="keyword">Keyword</option>
                            <option value="address">Address</option>
                        </select>
                    </div>
                    {renderSearchField()}
                    {mode === 'address' &&
                        <div className="form-group col-md-2 col-sm-12">
                            <label>Distance</label>
                            <select className="form-control" value={props.distance as any} onChange={(e) => props.setDistance(parseFloat(e.currentTarget.value))}>
                                <option value={0.5}>1/2 Mile</option>
                                <option value={1}>1 Mile</option>
                                <option value={2}>2 Miles</option>
                                <option value={3}>3 Miles</option>
                                <option value={4}>4 Miles</option>
                                <option value={10}>10 Miles</option>
                                <option value={20}>20 Miles</option>
                            </select>
                        </div>}
                    <button type="button" className="btn btn-primary d-none d-md-inline" style={{ marginTop: 32.5, height: 40 }} onClick={props.search}>
                        <i className="fas fa-search " />
                    </button>

                    {/* <input
                            id="searchBar"
                            type="text"
                            className="form-control col"
                            placeholder={props.intl.formatMessage(
                                messages.bryanKeywordSearchPrompt
                            )}
                            onKeyDown={props.handleKeyDown}
                            onChange={(e) => props.setTerm(e.currentTarget.value)}
                            value={props.term}
                        /> */}

                    {/* <input
                            id="searchBar"
                            type="text"
                            className="form-control"
                            placeholder={props.intl.formatMessage(
                                messages.bryanAddressSearchPrompt
                            )}
                            onKeyDown={props.handleKeyDown}
                            onChange={(e) => props.setTerm(e.currentTarget.value)}
                            value={props.term}
                        /> */}
                </div>
                <div className='row'>
                    <button type="button" className="btn btn-primary mt-2 d-md-none d-inline mx-auto" onClick={props.search}>
                        <span className="">Search</span>
                    </button>
                </div>
                {renderSearchFocusAreas()}
                <div className="row my-3">
                    <h5 className="col">More Filters</h5>
                </div>
                <div className='row'>
                    {renderSearchFilters()}
                </div>
                <div className='row'>
                    <button type="button" className="btn btn-primary mt-2 d-md-none d-inline mx-auto" onClick={props.search}>
                        <span className="">Search</span>
                    </button>
                </div>
            </div>
        </div>
    )
}



// WEBPACK FOOTER //
// ./src/public/search/BraynSearch.tsx